// ============================================================================
// AppPublique.js — Le montage ALLÉGÉ des pages publiques à jeton
// ============================================================================
// Demande Pierre (28/09/2026) : ouvrir un lien de convocation, de pouvoir,
// d'émargement ou de repas ne doit plus démarrer Aramis. Aucun écouteur
// Firestore, aucune donnée de séance, aucun service worker : la page ne parle
// qu'à SA Cloud Function, avec le jeton du lien.
//
// Ce qu'une page publique reçoit, et rien de plus :
//   • Firebase initialisé (import "./firebase" : les pages appellent
//     getFunctions() sur l'application par défaut) ;
//   • la feuille commune d'App.jsx (.card, .btn, .inp, socle mobile) et les
//     polices — installerFeuilleApp() ;
//   • le thème mémorisé (sans classe sur <body>, theme.css retombe en sombre)
//     et la taille de texte, la classe de coquille (téléphone / bureau) ;
//     sans thème mémorisé (premier visiteur), le réglage du téléphone
//     (prefers-color-scheme), suivi s'il change (décision D-L12-3, lot L12h).
// Les fournisseurs Toast / Confirm et le filet d'erreur sont déjà à la racine
// (src/index.js).
// ============================================================================
import { Suspense, useState } from "react";
import "./firebase";
import { installerFeuilleApp } from "./App";
import { applyTheme, themePagePublique, applyFontSize, getFontSize } from "./appShared";
import { installerClasseCoquille } from "./responsive";
import { lazyRetry } from "./lazyRetry";
import { BootGenese } from "./AuthBackdrop";

const ThemisPresencePage = lazyRetry(() => import("./ThemisPresencePage"));
const ThemisPouvoirPage = lazyRetry(() => import("./ThemisPouvoirPage"));
const ThemisEmargementPage = lazyRetry(() => import("./ThemisEmargementPage"));
const ThemisRepasPage = lazyRetry(() => import("./ThemisRepasPage"));
const PageExportComptable = lazyRetry(() => import("./PageExportComptable"));
const PageMediathequeInvite = lazyRetry(() => import("./mediatheque/PageMediathequeInvite"));
const PageEmargementFormation = lazyRetry(() => import("./mesFormations/PageEmargementFormation"));
const PageFormateur = lazyRetry(() => import("./mesFormations/PageFormateur"));
const PageMesFormationsInvite = lazyRetry(() => import("./mesFormations/PageMesFormationsInvite"));
const LectureFenetre = lazyRetry(() => import("./mesFormations/LectureFenetre"));
const PageMobiliteAccord = lazyRetry(() => import("./rhPole/PageMobiliteAccord"));
const PvSignaturePage = lazyRetry(() => import("./PvSignaturePage"));

let installe = false;
function installer() {
  if (installe || typeof document === "undefined") return;
  installe = true;
  installerFeuilleApp();
  applyFontSize(getFontSize());
  const theme = themePagePublique();
  applyTheme(theme);
  if (theme === "system") {
    try {
      const mq = window.matchMedia("(prefers-color-scheme: dark)");
      const maj = () => applyTheme("system");
      if (mq.addEventListener) mq.addEventListener("change", maj); else if (mq.addListener) mq.addListener(maj);
    } catch { /* navigateur sans matchMedia : le thème posé reste */ }
  }
  installerClasseCoquille();
  document.title = "Enio - Aramis";
  // Le module Firebase Functions se télécharge en même temps que la page,
  // au lieu de l'attendre au premier appel.
  import("firebase/functions").catch(() => {});
}

function Page({ route }) {
  const { vue, token, p, r } = route;
  if (vue === "themis-presence") return <ThemisPresencePage token={token} statutInitial={p} />;
  if (vue === "themis-pouvoir" || vue === "themis-pouvoir-confirmation") {
    return <ThemisPouvoirPage token={token} mode={vue === "themis-pouvoir-confirmation" ? "confirmation" : "donner"} reponseInitiale={r} />;
  }
  if (vue === "themis-emargement") return <ThemisEmargementPage token={token} />;
  if (vue === "themis-repas") return <ThemisRepasPage token={token} />;
  if (vue === "export-comptable") return <PageExportComptable token={token} />;
  if (vue === "formation-emargement") return <PageEmargementFormation token={token} />;
  if (vue === "formation-formateur") return <PageFormateur token={token} />;
  if (vue === "mes-formations-invite" || vue === "mes-formations-lien") return <PageMesFormationsInvite token={vue === "mes-formations-lien" ? "" : token} />;
  // Fenêtre de lecture d'un document (20/10/2026) : le jeton vient de la page
  // d'origine par postMessage, jamais de l'adresse.
  if (vue === "formation-document-jeton") return <LectureFenetre jeton />;
  if (vue === "rh-mobilite") return <PageMobiliteAccord token={token} />;
  // PV signé (03/11/2026) : signer depuis le lien du courriel, sans compte.
  if (vue === "pv-signature") return <PvSignaturePage token={token} />;
  if (vue === "mediatheque-invite") {
    // « Ne plus recevoir ces messages » : la page le PROPOSE, rien ne part sans un clic.
    let desabonner = false;
    try { desabonner = new URLSearchParams(window.location.search).get("desabonner") === "1"; } catch { desabonner = false; }
    return <PageMediathequeInvite token={token} desabonner={desabonner} />;
  }
  return null;
}

export default function AppPublique({ route }) {
  // Installation SYNCHRONE, avant le premier rendu : la page ne doit jamais
  // s'afficher un instant sans sa feuille ni dans le mauvais thème.
  useState(installer);
  return (
    <Suspense fallback={<BootGenese />}>
      <Page route={route} />
    </Suspense>
  );
}
